import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";

const normalizeRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const MODULES = [
  {
    title: "Material Quality Checks",
    path: "/employee/qa/checks",
    endpoint: "/employee/qa/supplier-dispatches",
    perms: ["quality:check:view", "quality:check:create"],
  },
  {
    title: "Rejection Notes",
    path: "/employee/qa/rejections",
    endpoint: "/employee/qa/rejections",
    perms: ["quality:reject:create"],
  },
  {
    title: "QA Approvals",
    path: "/employee/qa/approvals",
    endpoint: "/employee/qa/approvals",
    perms: ["quality:approve"],
  },
  {
    title: "QA Reports",
    path: "/employee/qa/reports",
    endpoint: "/employee/qa/reports",
    perms: ["quality:reports:view"],
  },
  {
    title: "Plant Quality Reviews",
    path: "/employee/plant/quality-reviews",
    endpoint: "/employee/plant/quality-reviews",
    perms: ["quality:check:view", "quality:check:create", "quality:approve"],
  },
];

const getQualityStatus = (row = {}) => row.qualityInspection?.status || "pending";

export default function QualityDepartmentDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [perms, setPerms] = useState([]);
  const [counts, setCounts] = useState({});
  const [dispatches, setDispatches] = useState([]);
  const [attendanceStatus, setAttendanceStatus] = useState("Not Marked");
  const [idStatus, setIdStatus] = useState("Not Generated");

  const visibleModules = useMemo(() => {
    const set = new Set(perms || []);
    return MODULES.filter((module) => module.perms.some((perm) => set.has(perm)));
  }, [perms]);

  const summary = useMemo(() => ({
    pending: dispatches.filter((row) => getQualityStatus(row) === "pending").length,
    passed: dispatches.filter((row) => getQualityStatus(row) === "passed").length,
    failed: dispatches.filter((row) => getQualityStatus(row) === "failed").length,
    recheck: dispatches.filter((row) => getQualityStatus(row) === "recheck").length,
  }), [dispatches]);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const meRes = await API.get("/auth/me");
      const nextPerms = meRes.data?.employeeRole?.permissions || meRes.data?.permissions || [];
      setPerms(Array.isArray(nextPerms) ? nextPerms : []);

      const attendanceReq = API.get("/attendance/today").catch(() => ({ data: null }));
      const idReq = API.get("/employee/my-idcard").catch(() => ({ data: null }));
      const dispatchReq = API.get("/employee/qa/supplier-dispatches").catch(() => ({ data: [] }));
      const moduleReqs = MODULES.map((module) =>
        API.get(module.endpoint)
          .then((res) => ({
            key: module.path,
            count: normalizeRows(res.data).length,
          }))
          .catch(() => ({ key: module.path, count: 0 }))
      );

      const [attendanceRes, idRes, dispatchRes, ...moduleRes] = await Promise.all([
        attendanceReq,
        idReq,
        dispatchReq,
        ...moduleReqs,
      ]);

      setAttendanceStatus(attendanceRes?.data?.status || "Not Marked");
      setIdStatus(idRes?.data?.idCardStatus || "Not Generated");
      setDispatches(normalizeRows(dispatchRes.data));
      setCounts(
        moduleRes.reduce((acc, row) => {
          acc[row.key] = row.count;
          return acc;
        }, {})
      );
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load quality dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="accounts-page">
      <div className="accounts-header">
        <div>
          <h4 className="mb-1">Quality Dashboard</h4>
          <div className="accounts-subtitle">
            Supplier material checking, rejection, approval aur QA reports ek jagah.
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button variant="outline-secondary" onClick={load}>Refresh</Button>
          <Button variant="success" onClick={() => navigate("/employee/attendance")}>Attendance</Button>
          <Button variant="dark" onClick={() => navigate("/employee/my-idcard")}>ID Card</Button>
        </div>
      </div>

      {err ? <Alert variant="danger">{err}</Alert> : null}

      <div className="row g-3 mb-3">
        <div className="col-md-3">
          <Card className="shadow-sm">
            <Card.Body>
              <div className="text-muted small">Today Attendance</div>
              <div className="fw-bold fs-5">{attendanceStatus}</div>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="shadow-sm">
            <Card.Body>
              <div className="text-muted small">ID Card Status</div>
              <div className="fw-bold fs-5">{idStatus}</div>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="shadow-sm">
            <Card.Body>
              <div className="text-muted small">Pending Material</div>
              <div className="fw-bold fs-5">{summary.pending}</div>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="shadow-sm">
            <Card.Body>
              <div className="text-muted small">Failed / Recheck</div>
              <div className="fw-bold fs-5">{summary.failed + summary.recheck}</div>
            </Card.Body>
          </Card>
        </div>
      </div>

      <div className="row g-3">
        {visibleModules.length === 0 ? (
          <div className="col-12">
            <Alert variant="warning" className="mb-0">
              No quality module permissions assigned.
            </Alert>
          </div>
        ) : (
          visibleModules.map((module) => (
            <div className="col-md-4 col-lg-3" key={module.path}>
              <Card className="shadow-sm h-100">
                <Card.Body className="d-flex flex-column">
                  <div className="text-muted small mb-1">Records</div>
                  <div className="fw-bold fs-4 mb-2">{counts[module.path] ?? 0}</div>
                  <div className="fw-semibold mb-3">{module.title}</div>
                  <Button
                    variant="outline-primary"
                    className="mt-auto"
                    onClick={() => navigate(module.path)}
                  >
                    Open
                  </Button>
                </Card.Body>
              </Card>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
